import React, { useState, useEffect } from 'react';
import { DynamicQuestion, SurveySection } from '../types';
import { getDynamicQuestions, saveDynamicQuestion, deleteDynamicQuestion, saveQuestionsOrder, getDynamicSections, saveSections } from '../services/storage';
import { Plus, Trash2, Edit2, Save, X, ChevronRight, Loader, ArrowUp, ArrowDown, FolderPlus, Folder, Calendar } from 'lucide-react';
import { useToast } from './Toast';

const INPUT_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'number', label: 'Number Input' },
  { value: 'date', label: 'Date Picker' },
  { value: 'yesno', label: 'Yes/No Switch' },
  { value: 'dropdown', label: 'Dropdown List' },
  { value: 'multiselect', label: 'Multi-Select (Checkbox)' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'image', label: 'Image Upload (Camera/File)' },
];

const QuestionManager: React.FC = () => {
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [sections, setSections] = useState<SurveySection[]>([]);
  const [editingQ, setEditingQ] = useState<DynamicQuestion | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isManagingSections, setIsManagingSections] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Section Edit State
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editSectionName, setEditSectionName] = useState('');

  const { addToast } = useToast();
  
  // Form State for Questions
  const [formData, setFormData] = useState<Partial<DynamicQuestion>>({
    label: '', type: 'text', section: 'sectionA', options: []
  });
  const [optionInput, setOptionInput] = useState('');

  // Form State for Adding Sections
  const [sectionForm, setSectionForm] = useState({ label: '', isRepeater: false });
  
  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [qs, secs] = await Promise.all([getDynamicQuestions(), getDynamicSections()]);
      setQuestions(qs);
      setSections(secs);
      // Default to first section if current formData.section is invalid
      if (secs.length > 0 && !secs.find(s => s.id === formData.section)) {
        setFormData(prev => ({ ...prev, section: secs[0].id }));
      }
    } catch (e) {
      addToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (q: DynamicQuestion) => {
    setEditingQ(q);
    // Deep copy options array to avoid reference issues
    setFormData({ ...q, options: q.options ? [...q.options] : [] });
    setIsAdding(true);
    setOptionInput('');
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this question? This may affect existing survey data.')) {
      try {
        await deleteDynamicQuestion(id);
        await refresh();
        addToast('Question deleted successfully', 'info');
      } catch (e) {
        addToast('Failed to delete question', 'error');
      }
    }
  };

  const handleAddOption = () => {
    if (optionInput.trim()) {
      setFormData(prev => ({
        ...prev,
        options: [...(prev.options || []), optionInput.trim()]
      }));
      setOptionInput('');
    }
  };

  const removeOption = (idx: number) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options?.filter((_, i) => i !== idx)
    }));
  };

  const handleMoveOption = (idx: number, direction: 'up' | 'down') => {
    const opts = [...(formData.options || [])];
    if (direction === 'up') {
        if (idx === 0) return;
        [opts[idx], opts[idx-1]] = [opts[idx-1], opts[idx]];
    } else {
        if (idx === opts.length - 1) return;
        [opts[idx], opts[idx+1]] = [opts[idx+1], opts[idx]];
    }
    setFormData({...formData, options: opts});
  };

  const handleMoveQuestion = async (id: string, direction: 'up' | 'down') => {
    const all = [...questions];
    const index = all.findIndex(q => q.id === id);
    if (index === -1) return;
    
    const currentQ = all[index];
    let targetIndex = -1;

    // Find neighbor in same section
    if (direction === 'up') {
        for (let i = index - 1; i >= 0; i--) {
            if (all[i].section === currentQ.section) {
                targetIndex = i;
                break;
            }
        }
    } else {
        for (let i = index + 1; i < all.length; i++) {
            if (all[i].section === currentQ.section) {
                targetIndex = i;
                break;
            }
        }
    }

    if (targetIndex !== -1) {
        [all[index], all[targetIndex]] = [all[targetIndex], all[index]];
        setQuestions(all); 
        try {
            await saveQuestionsOrder(all);
        } catch (e) {
            console.error(e);
            addToast('Failed to save order', 'error');
            refresh();
        }
    }
  };

  const handleSave = async () => {
    if (!formData.label || !formData.section || !formData.type) {
      alert('Please fill required fields');
      return;
    }

    const qId = editingQ ? editingQ.id : Date.now().toString();

    const q: DynamicQuestion = {
      id: qId,
      label: formData.label!,
      type: formData.type as any,
      section: formData.section!,
      options: (formData.type === 'dropdown' || formData.type === 'multiselect') ? formData.options : undefined,
      dependsOn: formData.dependsOn,
      visibleIf: formData.visibleIf,
      required: formData.required
    };

    const cleanQ = JSON.parse(JSON.stringify(q));

    try {
      await saveDynamicQuestion(cleanQ);
      await refresh();
      setIsAdding(false);
      setEditingQ(null);
      setFormData({ label: '', type: 'text', section: sections[0]?.id || 'sectionA', options: [] });
      addToast('Question saved successfully', 'success');
    } catch (e: any) {
      console.error(e);
      addToast('Failed to save question: ' + e.message, 'error');
    }
  };

  // --- Section Management Handlers ---

  const handleAddSection = async () => {
    if (!sectionForm.label) return;
    const newId = `section_${Date.now()}`;
    const newSection: SurveySection = {
      id: newId,
      label: sectionForm.label,
      isRepeater: sectionForm.isRepeater,
      order: sections.length
    };
    
    try {
      const updated = [...sections, newSection];
      await saveSections(updated);
      setSections(updated);
      setSectionForm({ label: '', isRepeater: false });
      addToast('Section added', 'success');
    } catch(e) {
      addToast('Failed to add section', 'error');
    }
  };

  const startEditSection = (section: SurveySection) => {
    setEditingSectionId(section.id);
    setEditSectionName(section.label);
  };

  const saveEditSection = async (id: string) => {
    if(!editSectionName.trim()) return;
    
    const updated = sections.map(s => s.id === id ? { ...s, label: editSectionName } : s);
    try {
      await saveSections(updated);
      setSections(updated);
      setEditingSectionId(null);
      addToast('Section renamed', 'success');
    } catch(e) {
      addToast('Failed to rename section', 'error');
    }
  };

  const handleDeleteSection = async (id: string) => {
    if (sections.length <= 1) {
      addToast('Cannot delete the last section', 'error');
      return;
    }
    // Check if questions exist in this section
    const hasQuestions = questions.some(q => q.section === id);
    if (hasQuestions) {
      alert('Cannot delete this section because it contains questions. Please move or delete the questions first.');
      return;
    }
    
    if (confirm('Are you sure you want to delete this section?')) {
      const updated = sections.filter(s => s.id !== id);
      await saveSections(updated);
      setSections(updated);
      addToast('Section deleted', 'info');
    }
  };

  const handleMoveSection = async (idx: number, direction: 'up' | 'down') => {
    const updated = [...sections];
    if (direction === 'up') {
      if (idx === 0) return;
      [updated[idx], updated[idx-1]] = [updated[idx-1], updated[idx]];
    } else {
      if (idx === updated.length - 1) return;
      [updated[idx], updated[idx+1]] = [updated[idx+1], updated[idx]];
    }
    
    // Update order property
    updated.forEach((s, i) => s.order = i);
    
    setSections(updated);
    await saveSections(updated);
  };

  // Filter potential parents for logic
  const potentialParents = questions.filter(q => 
    q.section === formData.section && 
    q.id !== (editingQ?.id) && 
    (q.type === 'dropdown' || q.type === 'yesno')
  );

  if (loading && questions.length === 0) return <div className="p-10 text-center"><Loader className="animate-spin inline mr-2"/> Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-20">
      
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-center border-b pb-4 gap-4">
        <h2 className="text-2xl font-bold text-gray-800">Survey Manager</h2>
        <div className="flex space-x-2">
          <button 
            onClick={() => setIsManagingSections(!isManagingSections)}
            className={`px-4 py-2 rounded-lg flex items-center border ${isManagingSections ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-white border-gray-300 hover:bg-gray-50'}`}
          >
            <FolderPlus size={18} className="mr-2"/> Manage Sections
          </button>
          <button 
            onClick={() => { setIsAdding(true); setEditingQ(null); setFormData({ label: '', type: 'text', section: sections[0]?.id, options: [] }); }}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-emerald-700"
          >
            <Plus size={18} className="mr-2"/> Add Question
          </button>
        </div>
      </div>

      {/* Section Manager UI */}
      {isManagingSections && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 animate-in fade-in slide-in-from-top-2">
           <h3 className="font-bold text-blue-900 mb-4 flex items-center"><Folder className="mr-2"/> Manage Survey Sections</h3>
           
           <div className="flex flex-col md:flex-row gap-4 mb-6 border-b border-blue-200 pb-6">
              <div className="flex-1">
                 <label className="block text-xs font-bold text-blue-800 mb-1">New Section Name</label>
                 <input 
                   className="w-full border p-2 rounded outline-none" 
                   placeholder="e.g. Section E: Education"
                   value={sectionForm.label}
                   onChange={e => setSectionForm({...sectionForm, label: e.target.value})}
                 />
              </div>
              <div className="flex items-center pt-5">
                 <label className="flex items-center cursor-pointer space-x-2">
                    <input 
                      type="checkbox" 
                      className="w-4 h-4"
                      checked={sectionForm.isRepeater}
                      onChange={e => setSectionForm({...sectionForm, isRepeater: e.target.checked})}
                    />
                    <span className="text-sm text-blue-900">Allow Multiple Entries? (Like Members)</span>
                 </label>
              </div>
              <div className="pt-5">
                 <button onClick={handleAddSection} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-bold">Add Section</button>
              </div>
           </div>

           <div className="space-y-2">
              {sections.map((sec, idx) => (
                <div key={sec.id} className="flex items-center justify-between bg-white p-3 rounded shadow-sm border border-blue-100">
                   <div className="flex items-center space-x-3 w-full">
                      <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded">{idx + 1}</span>
                      
                      {editingSectionId === sec.id ? (
                        <div className="flex items-center space-x-2 flex-1">
                          <input 
                            className="border p-1 rounded text-sm w-full" 
                            value={editSectionName} 
                            onChange={(e) => setEditSectionName(e.target.value)}
                            autoFocus
                          />
                          <button onClick={() => saveEditSection(sec.id)} className="text-green-600 hover:text-green-800"><Save size={16}/></button>
                          <button onClick={() => setEditingSectionId(null)} className="text-red-500 hover:text-red-700"><X size={16}/></button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                           <span className="font-medium text-gray-800">{sec.label}</span>
                           <button onClick={() => startEditSection(sec)} className="text-gray-400 hover:text-blue-500"><Edit2 size={14}/></button>
                        </div>
                      )}
                      
                      {sec.isRepeater && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded border border-purple-200 whitespace-nowrap">Repeater</span>}
                   </div>
                   <div className="flex items-center space-x-1 pl-4">
                      <button onClick={() => handleMoveSection(idx, 'up')} disabled={idx === 0} className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30"><ArrowUp size={16}/></button>
                      <button onClick={() => handleMoveSection(idx, 'down')} disabled={idx === sections.length - 1} className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30"><ArrowDown size={16}/></button>
                      <button onClick={() => handleDeleteSection(sec.id)} className="p-1 text-gray-400 hover:text-red-600 ml-2"><Trash2 size={16}/></button>
                   </div>
                </div>
              ))}
           </div>
        </div>
      )}

      {/* Add Question Form */}
      {isAdding && (
        <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-emerald-100 animate-in fade-in slide-in-from-top-4">
          <div className="flex justify-between mb-6">
            <h3 className="text-xl font-bold text-emerald-800">{editingQ ? 'Edit Question' : 'New Question'}</h3>
            <button onClick={() => setIsAdding(false)} className="text-gray-400 hover:text-gray-600"><X size={24}/></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2 md:col-span-1">
               <label className="block text-sm font-bold text-gray-700 mb-1">Question Text</label>
               <input 
                 className="w-full border p-2 rounded focus:ring-2 focus:ring-emerald-500 outline-none" 
                 value={formData.label} 
                 onChange={e => setFormData({...formData, label: e.target.value})}
                 placeholder="e.g., What is your occupation?"
                 autoComplete="off" 
                 name="question_label_input"
               />
            </div>

            <div>
               <label className="block text-sm font-bold text-gray-700 mb-1">Section</label>
               <select 
                 className="w-full border p-2 rounded focus:ring-2 focus:ring-emerald-500 outline-none bg-white"
                 value={formData.section} 
                 onChange={e => setFormData({...formData, section: e.target.value})}
               >
                 {sections.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
               </select>
            </div>

            <div>
               <label className="block text-sm font-bold text-gray-700 mb-1">Input Type</label>
               <select 
                 className="w-full border p-2 rounded focus:ring-2 focus:ring-emerald-500 outline-none bg-white"
                 value={formData.type} 
                 onChange={e => setFormData({...formData, type: e.target.value as any})}
               >
                 {INPUT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
               </select>
            </div>

            <div className="flex items-center mt-6">
               <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="w-5 h-5 accent-emerald-600"
                    checked={formData.required}
                    onChange={e => setFormData({...formData, required: e.target.checked})}
                  />
                  <span className="font-bold text-gray-700">Required Field</span>
               </label>
            </div>

            {/* Dropdown/Multiselect Builder */}
            {(formData.type === 'dropdown' || formData.type === 'multiselect') && (
              <div className="col-span-2 bg-yellow-50 p-4 rounded border border-yellow-200">
                <label className="block text-sm font-bold text-yellow-800 mb-2">Manage Options</label>
                <div className="flex gap-2 mb-3">
                   <input 
                     className="flex-1 border border-yellow-300 p-2 rounded focus:ring-2 focus:ring-yellow-500 outline-none" 
                     value={optionInput}
                     onChange={e => setOptionInput(e.target.value)}
                     placeholder="Type option and press Add"
                     onKeyDown={e => e.key === 'Enter' && handleAddOption()}
                     autoComplete="off"
                   />
                   <button onClick={handleAddOption} className="bg-yellow-600 text-white px-4 rounded font-bold hover:bg-yellow-700">Add</button>
                </div>
                
                {/* Options List */}
                <div className="space-y-2 mt-2">
                  {formData.options?.map((opt, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white border border-yellow-300 p-2 rounded text-sm shadow-sm group">
                       <span className="text-yellow-900 font-medium">{opt}</span>
                       <div className="flex items-center space-x-1">
                          <button 
                             onClick={() => handleMoveOption(idx, 'up')} 
                             disabled={idx === 0}
                             className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                             title="Move Up"
                          >
                             <ArrowUp size={14}/>
                          </button>
                          <button 
                             onClick={() => handleMoveOption(idx, 'down')} 
                             disabled={!formData.options || idx === formData.options.length - 1}
                             className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                             title="Move Down"
                          >
                             <ArrowDown size={14}/>
                          </button>
                          <button onClick={() => removeOption(idx)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded ml-1">
                             <X size={16}/>
                          </button>
                       </div>
                    </div>
                  ))}
                  {(!formData.options || formData.options.length === 0) && <span className="text-gray-400 italic text-sm">No options added yet.</span>}
                </div>
              </div>
            )}

            {/* Dependency Config */}
            <div className="col-span-2 bg-blue-50 p-4 rounded border border-blue-200">
               <h4 className="font-bold text-blue-800 mb-3 flex items-center"><ChevronRight size={16}/> Conditional Logic (Optional)</h4>
               <p className="text-xs text-blue-600 mb-3">Make this question visible only when another question has a specific value.</p>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-blue-700 mb-1">Depends On Question</label>
                    <select 
                      className="w-full border p-2 rounded bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                      value={formData.dependsOn || ''}
                      onChange={e => setFormData({...formData, dependsOn: e.target.value})}
                    >
                      <option value="">-- Always Visible --</option>
                      {potentialParents.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                  </div>
                  
                  {formData.dependsOn && (
                    <div>
                      <label className="block text-sm font-bold text-blue-700 mb-1">Visible When Value Is</label>
                      {(() => {
                        const parent = questions.find(q => q.id === formData.dependsOn);
                        if (parent?.type === 'yesno') {
                          return (
                            <select 
                              className="w-full border p-2 rounded bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                              value={String(formData.visibleIf)}
                              onChange={e => setFormData({...formData, visibleIf: e.target.value === 'true'})}
                            >
                              <option value="">Select...</option>
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          );
                        } else if (parent?.type === 'dropdown') {
                          return (
                            <select 
                              className="w-full border p-2 rounded bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                              value={String(formData.visibleIf)}
                              onChange={e => setFormData({...formData, visibleIf: e.target.value})}
                            >
                              <option value="">Select Value</option>
                              {parent.options?.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          );
                        }
                        return <p className="text-red-500 text-sm">Parent invalid type</p>;
                      })()}
                    </div>
                  )}
               </div>
            </div>

            <div className="col-span-2 pt-4 flex justify-end gap-4 border-t mt-2">
               <button onClick={() => setIsAdding(false)} className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">Cancel</button>
               <button onClick={handleSave} className="px-8 py-2 bg-emerald-600 text-white rounded font-bold shadow hover:bg-emerald-700">Save Question</button>
            </div>
          </div>
        </div>
      )}

      {/* List View by Section */}
      {sections.map(section => {
        const sectionQs = questions.filter(q => q.section === section.id);
        return (
          <div key={section.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b flex justify-between items-center">
               <h3 className="font-bold text-gray-700 flex items-center">
                  {section.label}
                  <button 
                     onClick={() => startEditSection(section)}
                     className="ml-2 text-gray-400 hover:text-blue-600"
                     title="Rename Section"
                  >
                     <Edit2 size={14} />
                  </button>
               </h3>
               <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded text-xs">{sectionQs.length} fields</span>
            </div>
            <div className="divide-y divide-gray-100">
               {sectionQs.map((q, idx) => (
                 <div key={q.id} className="p-4 flex items-center justify-between hover:bg-gray-50 group">
                    <div className="flex items-center space-x-3">
                       <div className="flex flex-col space-y-1 mr-2 opacity-50 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => handleMoveQuestion(q.id, 'up')}
                            disabled={idx === 0} 
                            className="p-1 hover:bg-gray-200 rounded disabled:opacity-20 cursor-pointer disabled:cursor-default text-gray-600"
                          >
                            <ArrowUp size={16}/>
                          </button>
                          <button 
                            onClick={() => handleMoveQuestion(q.id, 'down')}
                            disabled={idx === sectionQs.length - 1} 
                            className="p-1 hover:bg-gray-200 rounded disabled:opacity-20 cursor-pointer disabled:cursor-default text-gray-600"
                          >
                            <ArrowDown size={16}/>
                          </button>
                       </div>
                       <div>
                         <div className="font-semibold text-gray-800 flex items-center">
                           {q.label}
                           {q.required && <span className="text-red-500 ml-1" title="Required">*</span>}
                           {q.dependsOn && <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-1.5 rounded border border-blue-200">Conditional</span>}
                         </div>
                         <div className="text-xs text-gray-500 mt-1 uppercase tracking-wide">
                           {q.type} { (q.type === 'dropdown' || q.type === 'multiselect') && `• ${q.options?.length || 0} options`}
                         </div>
                       </div>
                    </div>
                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                       <button onClick={() => handleEdit(q)} className="p-2 text-blue-500 hover:bg-blue-50 rounded" title="Edit"><Edit2 size={18}/></button>
                       <button onClick={() => handleDelete(q.id)} className="p-2 text-red-500 hover:bg-red-50 rounded" title="Delete"><Trash2 size={18}/></button>
                    </div>
                 </div>
               ))}
               {sectionQs.length === 0 && <div className="p-6 text-center text-gray-400 italic">No questions in this section</div>}
            </div>
          </div>
        )
      })}
    </div>
  );
};

export default QuestionManager;